import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { PhoneInputComponent } from '../design-system/PhoneInput';
import { Input } from '../design-system/Input';
import { Button } from '../design-system/Button';
import { useAppStore } from '../../store/appStore';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isMobile = screenWidth < 768;

interface SendDatasetModalProps {
  visible: boolean;
  onClose: () => void;
  onMessageSent?: () => void;
  defaultSetId?: string; // Optional: pre-select a dataset
}

export const SendDatasetModal: React.FC<SendDatasetModalProps> = ({
  visible,
  onClose,
  onMessageSent,
  defaultSetId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { user, currentBusiness, currentSubscription } = useAppStore();
  const { navigateTo, showNotification } = useAppContext();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [selectedSetId, setSelectedSetId] = useState<string>(defaultSetId || '');
  const [quickReplySets, setQuickReplySets] = useState<any[]>([]);
  const [loadingSets, setLoadingSets] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const phoneInputRef = React.useRef<any>(null);

  // Load quick reply sets when modal opens
  useEffect(() => {
    if (visible) {
      loadQuickReplySets();
      if (defaultSetId) {
        setSelectedSetId(defaultSetId);
      }
    }
  }, [visible, defaultSetId]);

  const loadQuickReplySets = async () => {
    try {
      setLoadingSets(true);
      const response = await apiService.getQuickReplySets({
        isActive: true,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      if (response.statusCode === 1000 && response.data) {
        setQuickReplySets(response.data.quickReplySets || []);
      } else {
        showNotification('error', 'Error', 'Failed to load datasets');
      }
    } catch (error: any) {
      console.error('Error loading quick reply sets:', error);
      showNotification('error', 'Error', error.message || 'Failed to load datasets');
    } finally {
      setLoadingSets(false);
    }
  };

  const getWhatsAppPhoneNumber = () => {
    if (!currentSubscription) return null;
    
    const wabuService = currentSubscription?.services?.wabu as any;
    if (wabuService?.metadata?.whatsapp_phone?.length > 0) {
      const phone = wabuService.metadata.whatsapp_phone[0];
      return phone.whatsapp_phone_number || phone.phone || phone.phone_number || 'Unknown';
    }
    return null;
  };

  const handleSendDataset = async () => {
    if (!phoneNumber.trim()) {
      Alert.alert('Missing Information', 'Please enter a phone number.');
      return;
    }

    if (!message.trim()) {
      Alert.alert('Missing Information', 'Please enter a message to send first.');
      return;
    }

    if (!selectedSetId) {
      Alert.alert('Missing Information', 'Please select a dataset.');
      return;
    }

    if (!currentBusiness || !currentSubscription) {
      Alert.alert('Configuration Error', 'No business or subscription found.');
      return;
    }

    try {
      setIsSending(true);
      
      // Get formatted phone number
      let formattedPhoneNumber = phoneNumber;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          formattedPhoneNumber = phoneData.formattedNumber;
        } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
          formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
        }
      }

      // Ensure phone number starts with +
      if (!formattedPhoneNumber.startsWith('+')) {
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.dialCode) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        } else {
          formattedPhoneNumber = `+${phoneNumber}`;
        }
      }

      // Validate phone number
      if (formattedPhoneNumber.length < 8) {
        Alert.alert('Invalid Phone Number', 'Please enter a valid phone number.');
        return;
      }

      // Get WhatsApp credentials
      const wabuService = currentSubscription?.services?.wabu as any;
      
      if (!wabuService?.metadata?.whatsapp_phone?.length) {
        Alert.alert('Configuration Error', 'No WhatsApp phone number configured for this business.');
        return;
      }

      // Send dataset directly via API (sends text message first, then interactive dataset)
      const response = await apiService.sendDatasetToPhone(selectedSetId, formattedPhoneNumber, message.trim());

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Dataset sent successfully!');
        
        // Reset form
        setPhoneNumber('');
        setMessage('');
        setSelectedSetId(defaultSetId || '');
        
        onClose();
        if (onMessageSent) {
          onMessageSent();
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to send dataset';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error) {
      console.error('Error initiating dataset send:', error);
      Alert.alert('Error', 'Failed to initiate dataset send. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setMessage('');
    setSelectedSetId(defaultSetId || '');
    onClose();
  };

  const whatsappPhone = getWhatsAppPhoneNumber();

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.whatsappIcon}>
                <Ionicons name="logo-whatsapp" size={24} color="white" />
              </View>
              <Text style={styles.title}>Send Dataset</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Notification */}
            {whatsappPhone && (
              <View style={styles.notificationContainer}>
                <Ionicons name="information-circle" size={20} color={colors.warning} style={styles.notificationIcon} />
                <Text style={styles.notificationText}>
                  You need to send a message first to {whatsappPhone} from the recipient's number before sending the interactive dataset.
                </Text>
              </View>
            )}

            <View style={styles.formContainer}>
              <View style={styles.phoneInputSection}>
                <Text style={styles.phoneLabel}>Phone Number *</Text>
                <PhoneInputComponent
                  ref={phoneInputRef}
                  value={phoneNumber}
                  onChangeText={setPhoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Enter phone number"
                  style={styles.phoneInput}
                />
                {!phoneNumber.trim() && isSending && (
                  <Text style={styles.errorText}>Phone number is required</Text>
                )}
              </View>

              <Input
                label="Message *"
                value={message}
                onChangeText={setMessage}
                placeholder="Enter message to send first"
                multiline
                numberOfLines={3}
                required
                error={!message.trim() && isSending ? 'Message is required' : undefined}
              />

              <View style={styles.selectContainer}>
                <Text style={styles.selectLabel}>Select Dataset *</Text>
                {loadingSets ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.loadingText}>Loading datasets...</Text>
                  </View>
                ) : (
                  <ScrollView style={styles.datasetList} nestedScrollEnabled>
                    {quickReplySets.length === 0 ? (
                      <Text style={styles.emptyText}>No active datasets available</Text>
                    ) : (
                      quickReplySets.map((set) => (
                        <TouchableOpacity
                          key={set._id}
                          style={[
                            styles.datasetOption,
                            selectedSetId === set._id && styles.datasetOptionSelected,
                          ]}
                          onPress={() => setSelectedSetId(set._id)}
                        >
                          <View style={styles.datasetOptionContent}>
                            <Ionicons
                              name={selectedSetId === set._id ? 'radio-button-on' : 'radio-button-off'}
                              size={20}
                              color={selectedSetId === set._id ? colors.primary : colors.text.secondary}
                            />
                            <View style={styles.datasetOptionInfo}>
                              <Text style={[
                                styles.datasetOptionName,
                                selectedSetId === set._id && styles.datasetOptionNameSelected,
                              ]}>
                                {set.name}
                              </Text>
                              {set.description && (
                                <Text style={styles.datasetOptionDescription} numberOfLines={1}>
                                  {set.description}
                                </Text>
                              )}
                              <Text style={styles.datasetOptionMeta}>
                                {set.itemCount || set.items?.length || 0} items
                              </Text>
                            </View>
                          </View>
                        </TouchableOpacity>
                      ))
                    )}
                  </ScrollView>
                )}
                {!selectedSetId && isSending && (
                  <Text style={styles.errorText}>Please select a dataset</Text>
                )}
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Button
              variant="outlined"
              onPress={handleClose}
              disabled={isSending}
              style={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSendDataset}
              disabled={isSending || !phoneNumber.trim() || !message.trim() || !selectedSetId}
              loading={isSending}
              style={styles.sendButton}
            >
              <Ionicons name="send" size={16} color="white" style={styles.buttonIcon} />
              Send Dataset
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  modalContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.lg,
    width: isMobile ? '95%' : '85%',
    maxWidth: isMobile ? screenWidth - (spacing.lg * 2) : 600,
    maxHeight: '85%',
    elevation: 8,
    shadowColor: colors.literal.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  whatsappIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#25D366',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    flex: 1,
  },
  closeButton: {
    padding: spacing.sm,
    marginLeft: spacing.sm,
  },
  content: {
    padding: spacing.lg,
  },
  notificationContainer: {
    flexDirection: 'row',
    backgroundColor: withAlpha(colors.warning, 0.13),
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: colors.warning,
  },
  notificationIcon: {
    marginRight: spacing.sm,
    marginTop: 2,
  },
  notificationText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 20,
  },
  formContainer: {
    gap: spacing.lg,
  },
  phoneInputSection: {
    marginBottom: spacing.sm,
  },
  phoneLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  phoneInput: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  selectContainer: {
    marginTop: spacing.sm,
  },
  selectLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    gap: spacing.sm,
  },
  loadingText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  datasetList: {
    maxHeight: 200,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.secondary,
  },
  datasetOption: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  datasetOptionSelected: {
    backgroundColor: colors.primaryTransparent,
  },
  datasetOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  datasetOptionInfo: {
    flex: 1,
  },
  datasetOptionName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  datasetOptionNameSelected: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  datasetOptionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.xs / 2,
  },
  datasetOptionMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    padding: spacing.lg,
    fontFamily: typography.fontFamily.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  sendButton: {
    flex: 1,
    backgroundColor: '#25D366',
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
});

